import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import ScrollToTop from './ScrollToTop';
import { 
  Calculator, 
  Clock, 
  Zap, 
  ChevronDown, 
  ChevronUp, 
  Check, 
  ArrowRight, 
  Activity, 
  Eye, 
  Edit, 
  Users, 
  Settings, 
  MessageCircle, 
  Handshake, 
  ShieldCheck, 
  Search
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import MetodologiaPage from './pages/metodologia';
import CalidadPage from './pages/calidad';
import PrivacidadPage from './pages/privacidad';
import SeguridadPage from './pages/seguridad';

import WhatsappPage from './pages/whatsapp';
import WhatsappApiPage from './pages/whatsapp-api';
import TelefoniaPage from './pages/telefonia';
import DispersionTecnologicaPage from './pages/dispersion-tecnologica';
import OnPremiseVsCloudPage from './pages/oncloud-vs-onpremise';
import IaLocalPage from './pages/inteligencia-artificial-local';
import SistemasBackupPage from './pages/sistema-de-backup';
import EditorialDigitalPage from './pages/editorial-digital';
import GuiaIntegracionPage from './pages/guia-de-integracion';
import BlogPage from './pages/blog';
import BlogHPage from './pages/blogh';
import RenacimientoPage from './pages/renacimiento';
import NexbotPage from './pages/nexbot';
import NexbotContactPage from './pages/nexbot-contact';
import NexbotDemoPage from './pages/nexbot-demo';
import NexbotEulaPage from './pages/nexbot-eula';
import NexbotOpenrouterPage from './pages/nexbot-openrouter';
import NexbotPrivacidadPage from './pages/nexbot-privacidad';
import SdiPage from './pages/sdi';
import DapPage from './pages/dap';
import SmcPage from './pages/smc';
import SoportePage from './pages/soporte';
import ApiToolPage from './pages/api-tool';
import MicroservicioPage from './pages/microservicio';
import Bitrix24Page from './pages/bitrix24';
import IniciaConBitrix24Page from './pages/inicia-con-bitrix24';
import OmnicanalidadPage from './pages/omnicanalidad';
import ReclutamientoPersonalPage from './pages/reclutamiento-de-personal';
import ClinicasPage from './pages/clinicas';
import NextshotPage from './pages/nextshot';
import NextshotPrivacyPage from './pages/nextshot-privacy';
import WatokPage from './pages/watok';
import WatokSecurityPolicyPage from './pages/watok-security-policy';
import SitemapPage from './pages/sitemap';
import AppsPage from './pages/apps';
import NotexPage from './pages/notex';
import NextpdfPage from './pages/nextpdf';
import InfoodPage from './pages/infood';
import ClcPage from './pages/clc';
import Punku24Page from './pages/punku24';

import PostiPage from './pages/posti';
import PostiPrivacidadPage from './pages/posti-privacidad';
import PostiTerminosPage from './pages/posti-terminos';
import PostiEulaPage from './pages/posti-eula';

import ContactForm from './ContactForm';
import MultiPurposePopup from './components/MultiPurposePopup';
import ParticlesBackground from './components/ParticlesBackground';
import LanguageSwitcher from './components/LanguageSwitcher';
import Footer from './components/Footer';
import SEOManager from './components/SEOManager';

// --- ÍCONOS DE REDES SOCIALES (Usando Lucide-React para garantizar la compilación) ---
// Se eliminan las constantes de Data URL que causaron problemas visuales.

// Componente para manejar la carga de imágenes con fallback
const AvatarImage = ({ src, alt }) => {
  const [imgSrc, setImgSrc] = useState(src);

  // Fallback si la URL no funciona
  const handleError = () => {
    // Usar un placeholder genérico de Tailwind si la imagen falla
    setImgSrc("https://placehold.co/64x64/9ca3af/ffffff?text=Perfil");
  };

  // Restablecer el src si la prop cambia (aunque no debería en este caso, es buena práctica)
  useEffect(() => {
    setImgSrc(src);
  }, [src]);

  return (
    <img
      src={imgSrc}
      alt={alt}
      className="w-16 h-16 object-cover rounded-full mb-3 border-2 border-gray-100"
      onError={handleError}
    />
  );
};

// Componente principal de la Web
const NextcodersWebsite = () => {
  const { t, i18n } = useTranslation();
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [selectedProcesses, setSelectedProcesses] = useState([]);
  const [roiResults, setRoiResults] = useState(null);
  const [expandedPillar, setExpandedPillar] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Carrusel: Ref para el contenedor y estado para el índice
  const carouselRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  const testimonialsFromI18n = t('home.testimonials.items', { returnObjects: true });
  const testimonials = Array.isArray(testimonialsFromI18n) ? testimonialsFromI18n : [];
  
  const siteUrl = typeof window !== 'undefined' ? window.location.origin : 'https://nextcoders.work';
  const reviewLanguage = (i18n.resolvedLanguage || i18n.language || 'es').split('-')[0];
  const reviewsJsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: 'Nextcoders',
        url: siteUrl,
        logo: siteUrl + '/logo512.png',
        sameAs: [
          'https://x.com/NextcodersLLC',
          'https://www.linkedin.com/company/wearenextcoders/',
          'https://www.facebook.com/WeAreNextcodersLLC',
          'https://www.tiktok.com/@nextcodersllc'
        ]
      },
      {
        '@type': 'ItemList',
        name: 'Our Clients Voice',
        itemListOrder: 'http://schema.org/ItemListOrderAscending',
        itemListElement: testimonials.map((t, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'Review',
            inLanguage: reviewLanguage,
            reviewBody: t.quote,
            author: {
              '@type': 'Person',
              name: t.name,
              jobTitle: t.role,
              image: t.image,
              address: {
                '@type': 'PostalAddress',
                addressCountry: t.country
              }
            },
            itemReviewed: {
              '@type': 'Organization',
              name: 'Nextcoders',
              url: siteUrl
            },
            publisher: {
              '@type': 'Organization',
              name: 'Nextcoders'
            }
          }
        }))
      }
    ]
  };
  
  const NUM_TESTIMONIALS = testimonials.length;
  const [itemsPerView, setItemsPerView] = useState(3);
  
  // Lógica de Autoscroll para Testimonios
  useEffect(() => {
    // Determinar cuántos items mostrar
    const updateItemsPerView = () => {
      if (window.innerWidth < 640) { // sm
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) { // lg
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    updateItemsPerView();
    window.addEventListener('resize', updateItemsPerView);

    return () => window.removeEventListener('resize', updateItemsPerView);
  }, []);

  const MAX_SHIFT_INDEX = Math.max(0, NUM_TESTIMONIALS - itemsPerView);

  useEffect(() => {
    let interval = null;
    if (!isPaused && NUM_TESTIMONIALS > itemsPerView) {
      interval = setInterval(() => {
        setActiveIndex(prevIndex => {
          // Ir al siguiente índice
          const nextIndex = prevIndex + 1;
          // Si el siguiente índice supera el máximo posible, volver a 0
          if (nextIndex > MAX_SHIFT_INDEX) {
            return 0;
          }
          return nextIndex;
        });
      }, 4000); // Movimiento lento: 4 segundos
    }
    return () => clearInterval(interval);
  }, [isPaused, itemsPerView, MAX_SHIFT_INDEX, NUM_TESTIMONIALS]);

  
  // Reloj para el saludo
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);
  
  
  // Mapeo de procesos con iconos
  const processes = [
    { id: 'control', name: t('home.processes.control'), hours: 8, icon: Activity },
    { id: 'monitor', name: t('home.processes.monitor'), hours: 6, icon: Eye },
    { id: 'manage', name: t('home.processes.manage'), hours: 10, icon: Settings },
    { id: 'collaborate', name: t('home.processes.collaborate'), hours: 5, icon: Users },
    { id: 'process', name: t('home.processes.process'), hours: 12, icon: Edit },
    { id: 'communicate', name: t('home.processes.communicate'), hours: 4, icon: MessageCircle }
  ];

  const industries = [
    { id: 'tech', name: t('home.industries.tech') },
    { id: 'retail', name: t('home.industries.retail') },
    { id: 'mfg', name: t('home.industries.mfg') },
    { id: 'services', name: t('home.industries.services') },
    { id: 'health', name: t('home.industries.health') },
    { id: 'edu', name: t('home.industries.edu') },
    { id: 'finance', name: t('home.industries.finance') }
  ];

  const teamSizes = [
    '1-10', '11-50', '51-200', '201-500', '500+'
  ];
  
  const COSTO_HORA = 20;

  const calculateROI = () => {
    if (!selectedIndustry || !teamSize || selectedProcesses.length === 0) {
      setRoiResults(null);
      return;
    }
    
    const totalHours = selectedProcesses.reduce((sum, processId) => {
      const process = processes.find(p => p.id === processId);
      return sum + (process ? process.hours : 0);
    }, 0);

    // Multiplicador más progresivo para la estimación de equipo
    const teamMultiplier = teamSize === '1-10' ? 4 : teamSize === '11-50' ? 8 : teamSize === '51-200' ? 10 : teamSize === '201-500' ? 15 : 25;
    
    const monthlyHours = totalHours * 4 * teamMultiplier;
    const monthlySavings = monthlyHours * COSTO_HORA;

    setRoiResults({
      hours: monthlyHours,
      savings: monthlySavings,
      urgency: monthlyHours > 500 ? `${t('home.roi.urgency.high')} (${t('home.roi.urgency.high_sub')})` : monthlyHours > 200 ? `${t('home.roi.urgency.medium')} (${t('home.roi.urgency.medium_sub')})` : `${t('home.roi.urgency.low')} (${t('home.roi.urgency.low_sub')})`
    });
  };

  // Dependencia de useEffect para disparar el cálculo de ROI
  useEffect(() => {
    calculateROI();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedIndustry, teamSize, selectedProcesses]); // Dependencias explícitas

  const toggleProcess = (processId) => {
    setSelectedProcesses(prev => 
      prev.includes(processId) 
        ? prev.filter(id => id !== processId)
        : [...prev, processId]
    );
  };

  const getGreeting = () => {
    const hour = currentTime.getHours();
    if (hour < 12) return t('home.greetings.morning');
    if (hour < 18) return t('home.greetings.afternoon'); 
    return t('home.greetings.evening');
  };

  
  const handleNavLinkClick = (e, targetId) => {
    e.preventDefault();
    const targetElement = document.getElementById(targetId);

    if (targetElement) {
      const HEADER_OFFSET = 85; 

      window.scrollTo({
        top: targetElement.offsetTop - HEADER_OFFSET,
        behavior: 'smooth'
      });
    }
  };

  // Cálculo del Transform para el carrusel
  const itemWidthPercentage = 100 / itemsPerView;
  const transformValue = `translateX(-${activeIndex * itemWidthPercentage}%)`;
  
  const handleMouseEnter = () => setIsPaused(true);
  const handleMouseLeave = () => setIsPaused(false);

  return (
    <div className="min-h-screen font-inter">
      {/* Estilos para el carrusel (oculta barra de scroll) */}
      <style>{`
        .carousel-track::-webkit-scrollbar {
          display: none;
        }
        .carousel-track {
          -ms-overflow-style: none; /* IE and Edge */
          scrollbar-width: none; /* Firefox */
        }
      `}</style>
      
      {/* Header (Fijo y limpio) */}
      <header className="fixed top-0 w-full bg-white z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
          {/* Logo */}
          <a href="/" className="flex items-center">
            <img 
              src="https://imgur.com/nYWIvWJ.png" 
              alt="Nextcoders Logo" 
              className="h-8 transition-opacity duration-300"
              onError={(e) => { 
                e.target.onerror = null; 
                e.target.src="https://placehold.co/150x32/1f2937/ffffff?text=Nextcoders"; 
              }}
            />
          </a>
          
          <div className="flex items-center gap-4 md:gap-8">
            {/* Language Selector */}
            <LanguageSwitcher />

            {/* CTA que lleva al formulario de contacto */}
            <button 
              onClick={(e) => handleNavLinkClick(e, 'contact-title')}
              className="bg-[#141414] text-white px-4 md:px-6 py-2 rounded-lg hover:bg-gray-800 transition text-sm font-semibold whitespace-nowrap"
            >
              {t('common.free_diagnostic')}
            </button>
          </div>
        </div>
      </header>

      {/* Hero Section - sin blur para ver partículas nítidas */}
      <section className="pt-32 pb-20 px-4 sm:px-6 bg-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-gray-600 font-medium mb-6 text-xl">{getGreeting()}  </p>
            
            <h1 className="text-4xl md:text-6xl font-bold text-[#141414] mb-8 leading-tight tracking-tight">
              {t('home.hero.title')}<br className="hidden sm:block" />
              {t('home.hero.title_br')}
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 mb-10 leading-relaxed">
              {t('home.hero.subtitle')}
              <br />
              {t('home.hero.subtitle_br')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button 
                onClick={(e) => handleNavLinkClick(e, 'calculator-content')}
                className="bg-[#141414] text-white px-8 py-4 rounded-xl hover:bg-gray-800 transition font-semibold flex items-center justify-center gap-2"
              >
                <Calculator size={20} />
                {t('home.hero.cta_roi')}
              </button>
              <button 
                onClick={(e) => handleNavLinkClick(e, 'social-proof')}
                className="bg-white border-2 border-[#141414] text-[#141414] px-8 py-4 rounded-xl hover:bg-gray-50 transition font-semibold"
              >
                {t('home.hero.cta_success')}
              </button>
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid md:grid-cols-3 gap-6 mt-16 max-w-5xl mx-auto">
            <div className="bg-gray-50 p-6 rounded-2xl text-center border border-gray-200">
              <div className="text-3xl font-bold text-[#141414] mb-2">+36,000</div>
              <div className="text-gray-600">{t('home.stats.hours')}</div>
            </div>
            <div className="bg-gray-50 p-6 rounded-2xl text-center border border-gray-200">
              <div className="text-3xl font-bold text-[#141414] mb-2">+5</div>
              <div className="text-gray-600">{t('home.stats.countries')}</div>
            </div>
            <div className="bg-gray-50 p-6 rounded-2xl text-center border border-gray-200">
              <div className="text-3xl font-bold text-[#141414] mb-2">&lt;2h</div>
              <div className="text-gray-600">{t('home.stats.response')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ROI Calculator Section - sin blur, fondo transparente */}
      <section id="calculator" className="py-20 px-4 sm:px-6 bg-transparent">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#141414] mb-4">
              {t('home.roi.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('home.roi.subtitle')}
            </p>
          </div>

          {/* Calculator Card (ya es bg-white) */}
          <div id="calculator-content" className="bg-white rounded-3xl p-6 md:p-10 border border-gray-200">
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              {/* Select Industria */}
              <div>
                <label htmlFor="industry" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('home.roi.industry_label')}
                </label>
                <select
                  id="industry"
                  value={selectedIndustry}
                  onChange={(e) => setSelectedIndustry(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-[#141414] focus:border-[#141414] focus:outline-none transition"
                >
                  <option value="">{t('home.roi.industry_placeholder')}</option>
                  {industries.map(ind => (
                    <option key={ind.id} value={ind.id}>{ind.name}</option>
                  ))}
                </select>
              </div>

              {/* Select Tamaño de Equipo */}
              <div>
                <label htmlFor="teamSize" className="block text-sm font-medium text-gray-700 mb-2">
                  {t('home.roi.team_label')}
                </label>
                <select
                  id="teamSize"
                  value={teamSize}
                  onChange={(e) => setTeamSize(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-[#141414] focus:border-[#141414] focus:outline-none transition"
                >
                  <option value="">{t('home.roi.team_placeholder')}</option>
                  {teamSizes.map(size => (
                    <option key={size} value={size}>{size} {t('home.roi.team_suffix')}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mb-8">
              <label className="block text-sm font-medium text-gray-700 mb-4">
                {t('home.roi.processes_label')}
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {processes.map(process => {
                  const IconComponent = process.icon;
                  const isSelected = selectedProcesses.includes(process.id);
                  return (
                    <button
                      key={process.id}
                      onClick={() => toggleProcess(process.id)}
                      className={`p-4 rounded-xl border transition text-left flex flex-col items-start h-full ${
                        isSelected
                          ? 'border-[#141414] bg-gray-50 ring-2 ring-[#141414]'
                          : 'border-gray-200 hover:border-gray-400'
                      }`}
                    >
                      <div className="flex items-center justify-between w-full mb-1">
                        <span className="font-semibold text-[#141414]">{process.name}</span>
                        {isSelected && (
                          <Check className="text-[#141414]" size={20} />
                        )}
                      </div>
                      <div className="flex items-center justify-start gap-2">
                        <IconComponent className="text-gray-600" size={18} />
                        <div className="text-xs text-gray-500">{t('home.roi.process_hours', { hours: process.hours })}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ROI Results Display */}
            {roiResults && (
              <div className="bg-[#141414] rounded-xl p-6 md:p-8 text-white mt-8">
                {/* FÓRMULA */}
                <p className="text-center text-sm mb-4 text-gray-300 font-medium">
                  {t('home.roi.formula')}
                </p>
                
                {/* DISCLAIMER */}
                <p className="text-center text-xs mb-6 text-gray-500 italic border-b border-gray-700 pb-4">
                  {t('home.roi.disclaimer')}
                </p>

                <div className="grid sm:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-3xl md:text-4xl font-extrabold mb-1 text-white">
                      {roiResults.hours.toLocaleString()}
                    </div>
                    <div className="text-gray-300 text-sm">{t('home.roi.result_hours')}</div>
                  </div>
                  <div className="border-t sm:border-t-0 sm:border-l border-gray-700 pt-6 sm:pt-0 sm:pl-6">
                    <div className="text-3xl md:text-4xl font-extrabold mb-1 text-white">
                      ${roiResults.savings.toLocaleString()}
                    </div>
                    <div className="text-gray-300 text-sm">{t('home.roi.result_savings')}</div>
                  </div>
                  <div className="border-t sm:border-t-0 sm:border-l border-gray-700 pt-6 sm:pt-0 sm:pl-6">
                    <div className={`text-2xl md:text-3xl font-extrabold mb-1 ${
                      roiResults.urgency.includes('Alta') ? 'text-yellow-300' : 'text-white'
                    }`}>
                      {roiResults.urgency.includes('Alta') 
                        ? t('home.roi.urgency.high') 
                        : roiResults.urgency.includes('Media') 
                          ? t('home.roi.urgency.medium') 
                          : t('home.roi.urgency.low')}
                    </div>
                    <div className="text-gray-300 text-sm">
                      {roiResults.urgency.includes('Alta') 
                        ? t('home.roi.urgency.high_sub') 
                        : roiResults.urgency.includes('Media') 
                          ? t('home.roi.urgency.medium_sub') 
                          : t('home.roi.urgency.low_sub')}
                    </div>
                  </div>
                </div>
                <div className="mt-10 text-center">
                  <button 
                    onClick={(e) => handleNavLinkClick(e, 'contact-title')}
                    className="bg-white text-[#141414] px-8 py-4 rounded-lg font-bold hover:bg-gray-100 transition"
                  >
                    {t('home.roi.cta_discovery')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Mapa de Procesos - sin blur, fondo transparente */}
      <section className="py-20 px-4 sm:px-6 bg-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#141414] mb-4">
              {t('home.comparison.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('home.comparison.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Antes */}
            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200">
              <h3 className="text-2xl font-bold text-[#141414] mb-6 flex items-center gap-2">
                <Clock className="text-red-500" />
                {t('home.comparison.before_title')}
              </h3>
              <div className="space-y-4">
                {processes.map(process => (
                  <div key={`before-${process.id}`} className="flex items-center gap-3 p-2 border-b border-gray-200 last:border-b-0">
                    <div className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0"></div>
                    <div className="flex-1">
                      <div className="font-medium text-[#141414]">{process.name}</div>
                      <div className="text-sm text-gray-600">{process.hours} {t('home.comparison.hours_week')}</div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-gray-300">
                <div className="text-3xl font-bold text-red-600">
                  {processes.reduce((sum, p) => sum + p.hours, 0)} {t('home.comparison.hours_week')}
                </div>
                <div className="text-gray-600">{t('home.comparison.manual_tasks')}</div>
                
                <p className="mt-4 text-xs text-gray-600 italic">
                  {t('home.comparison.before_disclaimer')}
                </p>
              </div>
            </div>

            {/* Después */}
            <div className="bg-[#141414] rounded-2xl p-6 md:p-8 text-white border border-[#141414]">
              <h3 className="text-2xl font-bold mb-6 flex items-center gap-2 text-white">
                <Zap className="text-yellow-300" />
                {t('home.comparison.after_title')}
              </h3>
              <div className="space-y-4">
                {processes.map(process => (
                  <div key={`after-${process.id}`} className="flex items-center gap-3 p-2 border-b border-gray-700 last:border-b-0">
                    <div className="w-2 h-2 bg-yellow-300 rounded-full flex-shrink-0"></div>
                    <div className="flex-1">
                      <div className="font-medium text-white">{process.name}</div>
                      {/* Muestra el 80% del tiempo como "Recuperadas" */}
                      <div className="text-sm text-gray-300">{t('home.comparison.after_recovered', { hours: Math.round(process.hours * 0.8) })}</div>
                    </div>
                    <Check className="text-yellow-300" size={20} />
                  </div>
                ))}
              </div>
              <div className="mt-8 pt-6 border-t border-gray-700">
                <div className="text-3xl font-bold text-yellow-300">
                  {/* El total de horas recuperadas sigue siendo 36 (45 * 0.8) */}
                  {Math.round(processes.reduce((sum, p) => sum + p.hours, 0) * 0.8)} {t('home.comparison.hours_week')}
                </div>
                <div className="text-gray-300">{t('home.comparison.after_recovered_strategic')}</div>
                
                <p className="mt-4 text-xs text-gray-500 italic">
                  {t('home.comparison.after_disclaimer')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Principios de Valor - sin blur, fondo transparente */}
      <section id="pillars" className="py-20 px-4 sm:px-6 bg-transparent">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#141414] mb-4">
              {t('home.pillars.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('home.pillars.subtitle')}
            </p>
          </div>

          <div className="space-y-4 max-w-4xl mx-auto">
            {[
              {
                id: 'ally',
                icon: <Handshake className="text-[#141414]" size={32} />,
                title: t('home.pillars.items.ally.title'),
                description: t('home.pillars.items.ally.desc'),
                items: t('home.pillars.items.ally.list', { returnObjects: true })
              },
              {
                id: 'quality',
                icon: <ShieldCheck className="text-[#141414]" size={32} />,
                title: t('home.pillars.items.quality.title'),
                description: t('home.pillars.items.quality.desc'),
                items: t('home.pillars.items.quality.list', { returnObjects: true })
              },
              {
                id: 'proactivity',
                icon: <Search className="text-[#141414]" size={32} />,
                title: t('home.pillars.items.proactivity.title'),
                description: t('home.pillars.items.proactivity.desc'),
                items: t('home.pillars.items.proactivity.list', { returnObjects: true })
              }
            ].map(pillar => (
              <div key={pillar.id} className={`bg-white rounded-xl overflow-hidden border border-gray-200 transition ${expandedPillar === pillar.id ? 'border-[#141414]' : ''}`}>
                <button
                  onClick={() => setExpandedPillar(expandedPillar === pillar.id ? null : pillar.id)}
                  className={`w-full p-6 flex items-center justify-between hover:bg-gray-50 transition ${expandedPillar === pillar.id ? 'bg-gray-50' : 'bg-white'}`}
                >
                  <div className="flex items-center gap-4">
                    {pillar.icon}
                    <div className="text-left">
                      <h3 className="text-xl md:text-2xl font-bold text-[#141414]">{pillar.title}</h3>
                      <p className="text-gray-600 text-sm md:text-base">{pillar.description}</p>
                    </div>
                  </div>
                  {expandedPillar === pillar.id ? (
                    <ChevronUp className="text-[#141414] flex-shrink-0" size={24} />
                  ) : (
                    <ChevronDown className="text-gray-500 flex-shrink-0" size={24} />
                  )}
                </button>
                {expandedPillar === pillar.id && (
                  <div className="px-6 pb-6 border-t border-gray-100">
                    <ul className="list-disc pl-6 text-gray-700 leading-relaxed space-y-2 mt-4">
                      {pillar.items && pillar.items.map((item, idx) => (
                        <li key={idx}>{item}</li>
                      ))}
                    </ul>
                    {/* Botón de Metodología */}
                    <Link to="/metodologia" className="mt-4 text-[#141414] font-semibold flex items-center gap-2 hover:gap-3 transition-all text-sm">
                      {t('home.pillars.cta_methodology')} <ArrowRight size={16} />
                    </Link>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social Proof / Testimonios (Carrusel Automático) - FONDO TRANSPARENTE SIN BLUR */}
      <section 
        id="social-proof"
        className="py-20 px-4 sm:px-6 bg-transparent relative"
        style={{ backgroundColor: 'transparent' }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      > 
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(reviewsJsonLd) }} />
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#141414] mb-4">
              {t('home.testimonials.title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('home.testimonials.subtitle')}
            </p>
          </div>

          <div className="overflow-hidden -mx-3">
            <div 
              ref={carouselRef}
              className="flex transition-transform duration-700 ease-in-out carousel-track"
              style={{ transform: transformValue }}
            >
              {testimonials.map((testimonial, idx) => (
                <div
                  key={idx}
                  // Ajuste de ancho dinámico basado en itemsPerView
                  className="flex-shrink-0 px-3" 
                  style={{ width: `${itemWidthPercentage}%` }}
                >
                  <div className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#141414] transition h-full flex flex-col justify-between text-center">
                    
                    {/* Perfil */}
                    <div className="flex flex-col items-center mb-6 border-b border-gray-100 pb-4">
                      <AvatarImage src={testimonial.image} alt={`Foto de ${testimonial.name}`} />
                      <div className="font-bold text-[#141414] leading-snug">{testimonial.name}</div>
                      
                      {/* El truncate se aplica aquí para manejar títulos largos */}
                      <div className="text-sm text-gray-600 truncate max-w-full px-2">{testimonial.role}</div> 

                      <div className="text-xs text-gray-500 mt-1">
                        {testimonial.industry}
                      </div>
                    </div>
                    
                    {/* Cita */}
                    <p className="text-gray-700 text-base italic leading-relaxed flex-grow">
                      “{testimonial.quote}”
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Indicadores de posición */}
          {MAX_SHIFT_INDEX > 0 && (
            <div className="flex justify-center gap-2 mt-6">
              {Array.from({ length: MAX_SHIFT_INDEX + 1 }).map((_, i) => (
                <button
                  key={i}
                  aria-label={`Ir al testimonio ${i + 1}`}
                  onClick={() => setActiveIndex(i)}
                  className={`w-3 h-3 rounded-full ${
                    i === activeIndex ? 'bg-[#141414]' : 'bg-gray-300'
                  } transition`}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Contacto - sin blur, fondo transparente */}
      <section id="contact" className="py-20 px-4 sm:px-6 bg-transparent">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#141414] mb-4">
              {t('home.contact.alt_title')}
            </h2>
            <p className="text-lg text-gray-600">
              {t('home.contact.alt_subtitle')}
            </p>
          </div>

          <div className="bg-white rounded-3xl p-6 md:p-10 border border-gray-200">
            <div className="mb-8">
              <h3 id="contact-title" className="text-2xl font-bold text-[#141414] mb-4">{t('home.contact.card_title')}</h3>
              <p className="text-gray-600 mb-6">{t('home.contact.card_subtitle')}</p>
              
              {/* PUNTO DE ANCLAJE: Aqui es donde se renderiza el formulario de contacto. 
                  El componente carga Turnstile y gestiona el envio. */}
              <ContactForm />
            </div>
            
            {/* Contact Details */}
            <div className="mt-8 pt-8 border-t border-gray-200">
              <div className="grid sm:grid-cols-2 gap-6 text-center">
                <div className="pb-4 sm:pb-0 sm:border-r border-gray-200">
                  <div className="font-bold text-[#141414] mb-2">{t('home.contact.details.calls')}</div>
                  <div className="text-gray-700 text-sm">+1 (305) 707-5219 (USA)</div>
                </div>
                <div>
                  <div className="font-bold text-[#141414] mb-2">{t('home.contact.details.email')}</div>
                  <div className="text-gray-700 text-sm">hello@nextcoders.work</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

// Layout común que incluye header y footer
const MainLayout = ({ children }) => {
  const location = useLocation();
  const showPopupOnThisPage = location.pathname === '/';

  return (
    <div className="App relative">
      {/* Fondo de partículas dentro del mismo contexto de apilamiento */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <ParticlesBackground />
      </div>
      <div className="relative z-10">
        {/* Header se mantiene aquí si es común a todas las páginas */}
        {children}
        {/* Popup Multipropósito */}
        {showPopupOnThisPage ? <MultiPurposePopup /> : null}
        {/* Footer se mantiene aquí si es común a todas las páginas */}
        <Footer />
      </div>
    </div>
  );
};

const RoutePathNormalizer = () => {
  const location = useLocation();
  const normalizedPath = location.pathname && location.pathname !== '/'
    ? location.pathname.replace(/\/+$/, '') || '/'
    : '/';

  if (normalizedPath !== location.pathname) {
    return <Navigate to={`${normalizedPath}${location.search}${location.hash}`} replace />;
  }

  return null;
};

// Componente principal que maneja el routing
const App = () => {
  return (
    <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
      <RoutePathNormalizer />
      <SEOManager />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={
          <MainLayout>
            <NextcodersWebsite />
          </MainLayout>
        } />
        <Route path="/metodologia" element={
          <MainLayout>
            <MetodologiaPage />
          </MainLayout>
        } />
        <Route path="/calidad" element={
          <MainLayout>
            <CalidadPage />
          </MainLayout>
        } />
        <Route path="/privacidad" element={
          <MainLayout>
            <PrivacidadPage />
          </MainLayout>
        } />
        <Route path="/seguridad" element={
          <MainLayout>
            <SeguridadPage />
          </MainLayout>
        } />
        <Route path="/whatsapp" element={
          <MainLayout>
            <WhatsappPage />
          </MainLayout>
        } />
        <Route path="/telefonia" element={
          <MainLayout>
            <TelefoniaPage />
          </MainLayout>
        } />
        <Route path="/omnicanalidad" element={
          <MainLayout>
            <OmnicanalidadPage />
          </MainLayout>
        } />
        <Route path="/dispersion-tecnologica" element={
          <MainLayout>
            <DispersionTecnologicaPage />
          </MainLayout>
        } />
        <Route path="/on-premise-vs-on-cloud" element={
          <MainLayout>
            <OnPremiseVsCloudPage />
          </MainLayout>
        } />
        <Route path="/inteligencia-artificial-local" element={
          <MainLayout>
            <IaLocalPage />
          </MainLayout>
        } />
        <Route path="/sistemas-de-backup" element={
          <MainLayout>
            <SistemasBackupPage />
          </MainLayout>
        } />
        <Route path="/editorial-digital" element={
          <MainLayout>
            <EditorialDigitalPage />
          </MainLayout>
        } />
        <Route path="/blog" element={
          <MainLayout>
            <BlogPage />
          </MainLayout>
        } />
        <Route path="/sitemap" element={
          <MainLayout>
            <SitemapPage />
          </MainLayout>
        } />
        <Route path="/renacimiento" element={
          <MainLayout>
            <RenacimientoPage />
          </MainLayout>
        } />
        <Route path="/blogh" element={
          <MainLayout>
            <BlogHPage />
          </MainLayout>
        } />
        <Route path="/whatsapp-api" element={
          <MainLayout>
            <WhatsappApiPage />
          </MainLayout>
        } />
        <Route path="/guia-de-integracion" element={
          <MainLayout>
            <GuiaIntegracionPage />
          </MainLayout>
        } />
        <Route path="/nexbot" element={
          <MainLayout>
            <NexbotPage />
          </MainLayout>
        } />
        <Route path="/nexbot-contact" element={
          <MainLayout>
            <NexbotContactPage />
          </MainLayout>
        } />
        <Route path="/nexbot-demo" element={
          <MainLayout>
            <NexbotDemoPage />
          </MainLayout>
        } />
        <Route path="/nexbot-eula" element={
          <MainLayout>
            <NexbotEulaPage />
          </MainLayout>
        } />
        <Route path="/nexbot-openrouter" element={
          <MainLayout>
            <NexbotOpenrouterPage />
          </MainLayout>
        } />
        <Route path="/nexbot-privacidad" element={
          <MainLayout>
            <NexbotPrivacidadPage />
          </MainLayout>
        } />
        <Route path="/nexia" element={<Navigate to="/nexbot" replace />} />
        <Route path="/sdi" element={
          <MainLayout>
            <SdiPage />
          </MainLayout>
        } />
        <Route path="/dap" element={
          <MainLayout>
            <DapPage />
          </MainLayout>
        } />
        <Route path="/smc" element={
          <MainLayout>
            <SmcPage />
          </MainLayout>
        } />
        <Route path="/soporte" element={
          <MainLayout>
            <SoportePage />
          </MainLayout>
        } />
        <Route path="/api-tool" element={
          <MainLayout>
            <ApiToolPage />
          </MainLayout>
        } />
        <Route path="/microservicio" element={
          <MainLayout>
            <MicroservicioPage />
          </MainLayout>
        } />
        <Route path="/crm" element={
          <MainLayout>
            <Bitrix24Page />
          </MainLayout>
        } />
        <Route path="/bitrix24" element={<Navigate to="/crm" replace />} />
        <Route path="/inicia-con-bitrix24" element={
          <MainLayout>
            <IniciaConBitrix24Page />
          </MainLayout>
        } />
        <Route path="/reclutamiento-de-personal" element={
          <MainLayout>
            <ReclutamientoPersonalPage />
          </MainLayout>
        } />
        <Route path="/clinicas" element={
          <MainLayout>
            <ClinicasPage />
          </MainLayout>
        } />
        <Route path="/apps" element={
          <MainLayout>
            <AppsPage />
          </MainLayout>
        } />
        <Route path="/notex" element={
          <MainLayout>
            <NotexPage />
          </MainLayout>
        } />
        <Route path="/nextpdf" element={
          <MainLayout>
            <NextpdfPage />
          </MainLayout>
        } />
        <Route path="/infood" element={
          <MainLayout>
            <InfoodPage />
          </MainLayout>
        } />
        <Route path="/clc" element={
          <MainLayout>
            <ClcPage />
          </MainLayout>
        } />
        <Route path="/punku24" element={
          <MainLayout>
            <Punku24Page />
          </MainLayout>
        } />
        <Route path="/posti" element={
          <MainLayout>
            <PostiPage />
          </MainLayout>
        } />
        <Route path="/posti-privacidad" element={
          <MainLayout>
            <PostiPrivacidadPage />
          </MainLayout>
        } />
        <Route path="/posti-terminos" element={
          <MainLayout>
            <PostiTerminosPage />
          </MainLayout>
        } />
        <Route path="/posti-eula" element={
          <MainLayout>
            <PostiEulaPage />
          </MainLayout>
        } />
        <Route path="/nextshot/privacy" element={
          <MainLayout>
            <NextshotPrivacyPage />
          </MainLayout>
        } />
        <Route path="/watok" element={
          <MainLayout>
            <WatokPage />
          </MainLayout>
        } />
        <Route path="/watok-security-policy" element={
          <MainLayout>
            <WatokSecurityPolicyPage />
          </MainLayout>
        } />
        <Route path="/nextshot" element={
          <MainLayout>
            <NextshotPage />
          </MainLayout>
        } />
      </Routes>
    </Router>
  );
};

export default App;

